import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ExternalLink,
  FileCode,
  Github,
  Loader,
  X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { submitJobOperation, waitForJobResult } from '../../services/jobs';

const DEFAULT_GITHUB_EXPORT_FORM = {
  repository: '',
  branch: 'main',
  target_path: 'automation',
  commit_message: '',
  selected_only: false,
  include_package_lock: true,
  include_run_results: false,
  create_repository: false,
  new_repository_name: '',
  new_repository_owner: '',
  new_repository_description: '',
  new_repository_private: true,
};

function normalizeErrorDetail(detail) {
  if (detail === null || detail === undefined) return '';

  if (
    typeof detail === 'string'
    || typeof detail === 'number'
    || typeof detail === 'boolean'
  ) {
    const normalized = String(detail).trim();
    if (!normalized || normalized === '[object Object]') return '';
    return normalized;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => normalizeErrorDetail(item))
      .filter(Boolean)
      .join(' | ');
  }

  if (typeof detail === 'object') {
    const locParts = Array.isArray(detail?.loc)
      ? detail.loc.map((item) => String(item || '').trim()).filter(Boolean)
      : [];
    const msgText = normalizeErrorDetail(detail?.msg);
    if (locParts.length > 0 && msgText) {
      return `${locParts.join('.')}: ${msgText}`;
    }

    const preferredParts = [
      detail?.message,
      detail?.detail,
      detail?.reason,
      detail?.error,
      detail?.title,
      detail?.description,
      detail?.msg,
    ];
    for (const part of preferredParts) {
      const normalized = normalizeErrorDetail(part);
      if (normalized) return normalized;
    }

    try {
      const serialized = JSON.stringify(detail);
      return serialized && serialized !== '{}' ? serialized : '';
    } catch {
      return '';
    }
  }

  return '';
}

function resolveApiErrorMessage(errorData, fallback) {
  const normalized = normalizeErrorDetail(
    errorData?.detail ?? errorData?.error ?? errorData?.message ?? errorData
  );
  if (/\b(queue|queued|worker|bulk)\b/i.test(normalized)) {
    return fallback;
  }
  return normalized || fallback;
}

function resolveCaughtErrorMessage(error, fallback) {
  const normalized = normalizeErrorDetail(error?.message ?? error);
  if (/\b(queue|queued|worker|bulk)\b/i.test(normalized)) {
    return fallback;
  }
  return normalized || fallback;
}

export const GithubExportModal = ({
  allTests = [],
  selectedCases,
  pdfId,
  sectionNumber,
  onClose,
}) => {
  const [form, setForm] = useState(DEFAULT_GITHUB_EXPORT_FORM);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);
  const [connection, setConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  const selectedCaseSet = selectedCases instanceof Set ? selectedCases : new Set();

  const resolveCaseIds = () => {
    if (!form.selected_only) return null;
    return allTests
      .filter((testCase) => selectedCaseSet.has(testCase._case_key))
      .map((testCase) => String(testCase?.test_case_no || testCase?.testcase_no || testCase?.id || '').trim())
      .filter(Boolean);
  };

  const loadConnection = async () => {
    setConnectionLoading(true);
    setConnectionError(null);
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.GITHUB_STATUS}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(resolveApiErrorMessage(errorData, 'Failed to load GitHub connection'));
      }
      setConnection(await response.json());
    } catch (err) {
      setConnectionError(resolveCaughtErrorMessage(err, 'Failed to load GitHub connection'));
    } finally {
      setConnectionLoading(false);
    }
  };

  useEffect(() => {
    loadConnection();
    const handleMessage = (event) => {
      if (event?.data?.type === 'github-oauth-complete') {
        loadConnection();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const connectGithub = () => {
    setConnectionError(null);
    const returnUrl = window.location.href;
    const params = new URLSearchParams({ return_url: returnUrl });
    const popup = window.open(
      `${API_BASE_URL}${API_ENDPOINTS.GITHUB_OAUTH_START}?${params.toString()}`,
      'github-oauth',
      'width=720,height=760'
    );
    if (!popup) {
      window.location.href = `${API_BASE_URL}${API_ENDPOINTS.GITHUB_OAUTH_START}?${params.toString()}`;
      return;
    }

    const startedAt = Date.now();
    const pollId = window.setInterval(async () => {
      if (Date.now() - startedAt > 180000 || popup.closed) {
        window.clearInterval(pollId);
        await loadConnection();
      }
    }, 1000);
  };

  const disconnectGithub = async () => {
    setConnectionLoading(true);
    setConnectionError(null);
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.GITHUB_DISCONNECT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(resolveApiErrorMessage(errorData, 'Failed to disconnect GitHub'));
      }
      setConnection(await response.json());
    } catch (err) {
      setConnectionError(resolveCaughtErrorMessage(err, 'Failed to disconnect GitHub'));
    } finally {
      setConnectionLoading(false);
    }
  };

  const buildPayload = () => ({
    pdf_id: pdfId,
    section_number: Number(sectionNumber || 0),
    repository: form.create_repository ? null : (form.repository.trim() || null),
    branch: form.branch.trim() || 'main',
    target_path: form.target_path.trim() || 'automation',
    commit_message: form.commit_message.trim() || null,
    test_case_ids: resolveCaseIds(),
    include_package_lock: Boolean(form.include_package_lock),
    include_run_results: Boolean(form.include_run_results),
    create_repository: Boolean(form.create_repository),
    new_repository_name: form.new_repository_name.trim() || null,
    new_repository_owner: form.new_repository_owner.trim() || null,
    new_repository_description: form.new_repository_description.trim() || null,
    new_repository_private: Boolean(form.new_repository_private),
  });

  const previewExport = async () => {
    const resolvedSectionNumber = Number(sectionNumber || 0);
    if (!pdfId || !resolvedSectionNumber) return;
    const caseIds = resolveCaseIds();
    if (form.selected_only && !caseIds.length) {
      setError('Select at least one test case before exporting selected scripts only.');
      return;
    }
    setLoading('preview');
    setError(null);
    setResult(null);
    try {
      const params = new URLSearchParams({
        target_path: form.target_path.trim() || 'automation',
        include_package_lock: String(Boolean(form.include_package_lock)),
        include_run_results: String(Boolean(form.include_run_results)),
      });
      if (caseIds?.length) params.set('test_case_ids', caseIds.join(','));
      const response = await apiFetch(`${API_ENDPOINTS.PLAYWRIGHT_EXPORT_PREVIEW(pdfId, resolvedSectionNumber)}?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(resolveApiErrorMessage(errorData, 'Failed to preview GitHub export'));
      }
      setPreview(await response.json());
    } catch (err) {
      setError(resolveCaughtErrorMessage(err, 'Failed to preview GitHub export'));
    } finally {
      setLoading(null);
    }
  };

  const pushExport = async () => {
    const caseIds = resolveCaseIds();
    if (form.selected_only && !caseIds.length) {
      setError('Select at least one test case before exporting selected scripts only.');
      return;
    }
    if (form.create_repository && !form.new_repository_name.trim()) {
      setError('Enter a repository name before creating a new GitHub repository.');
      return;
    }
    if (!form.create_repository && !form.repository.trim()) {
      setError('Enter an existing repository as owner/repo or choose Create new repository.');
      return;
    }
    setLoading('export');
    setError(null);
    setResult(null);
    try {
      const { job } = await submitJobOperation(API_ENDPOINTS.PLAYWRIGHT_GITHUB_EXPORT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      }, { errorMessage: 'Unable to queue GitHub export.' });
      const snapshot = await waitForJobResult(job);
      const payload = snapshot.job.result_summary || {};
      setResult(payload);
      setPreview({
        exported_files: payload.exported_files || [],
        script_pom_map: payload.script_pom_map || {},
        warnings: payload.warnings || [],
        files_count: payload.files_exported || 0,
        target_path: payload.target_path,
      });
    } catch (err) {
      setError(resolveCaughtErrorMessage(err, 'Failed to push files to GitHub'));
    } finally {
      setLoading(null);
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-shell max-w-3xl overflow-hidden">
        <div className="border-b border-slate-700 bg-slate-900 px-5 py-4 text-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                GitHub Export
              </p>
              <h3 className="mt-1 text-lg font-bold">Push Playwright Files</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/30 bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Close GitHub export"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="max-h-[72vh] overflow-y-auto bg-white p-5 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">GitHub account</p>
                <p className="mt-1 text-xs text-slate-600">
                  {connectionLoading
                    ? 'Checking GitHub connection...'
                    : connection?.connected
                      ? `Connected as ${connection.github_login || 'GitHub user'}`
                      : connection?.fallback_available
                        ? 'Using server fallback until a user connects GitHub.'
                        : 'Connect GitHub to push files or create a repository.'}
                </p>
                {connectionError && (
                  <p className="mt-1 text-xs font-medium text-red-700">{connectionError}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={connectGithub}
                  disabled={connectionLoading || connection?.configured === false}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {connectionLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Github className="h-4 w-4" />}
                  {connection?.connected ? 'Reconnect' : 'Connect GitHub'}
                </button>
                {connection?.connected && (
                  <button
                    type="button"
                    onClick={disconnectGithub}
                    disabled={connectionLoading}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                )}
              </div>
            </div>
            {connection?.configured === false && (
              <p className="mt-2 text-xs font-medium text-amber-700">
                GitHub OAuth is not configured on the backend.
              </p>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {form.create_repository ? (
              <>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">New Repository Name</span>
                  <input
                    type="text"
                    value={form.new_repository_name}
                    onChange={(event) => updateForm('new_repository_name', event.target.value)}
                    placeholder="playwright-automation"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-600 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Owner or Organization</span>
                  <input
                    type="text"
                    value={form.new_repository_owner}
                    onChange={(event) => updateForm('new_repository_owner', event.target.value)}
                    placeholder={connection?.github_login || 'Defaults to connected user'}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-600 focus:outline-none"
                  />
                </label>
              </>
            ) : (
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Repository</span>
                <input
                  type="text"
                  value={form.repository}
                  onChange={(event) => updateForm('repository', event.target.value)}
                  placeholder="owner/repo or GitHub URL"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-600 focus:outline-none"
                />
              </label>
            )}
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Branch</span>
              <input
                type="text"
                value={form.branch}
                onChange={(event) => updateForm('branch', event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-600 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Target Folder</span>
              <input
                type="text"
                value={form.target_path}
                onChange={(event) => updateForm('target_path', event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-600 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Commit Message</span>
              <input
                type="text"
                value={form.commit_message}
                onChange={(event) => updateForm('commit_message', event.target.value)}
                placeholder="Generated automatically if blank"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-600 focus:outline-none"
              />
            </label>
            {form.create_repository && (
              <label className="block md:col-span-2">
                <span className="text-sm font-semibold text-slate-700">Repository Description</span>
                <input
                  type="text"
                  value={form.new_repository_description}
                  onChange={(event) => updateForm('new_repository_description', event.target.value)}
                  placeholder="Generated Playwright automation"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-600 focus:outline-none"
                />
              </label>
            )}
          </div>
          <div className="flex flex-wrap gap-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
            <label className="inline-flex items-center gap-2 font-medium text-slate-700">
              <input
                type="checkbox"
                checked={form.create_repository}
                onChange={(event) => updateForm('create_repository', event.target.checked)}
              />
              Create new repository
            </label>
            {form.create_repository && (
              <label className="inline-flex items-center gap-2 font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={form.new_repository_private}
                  onChange={(event) => updateForm('new_repository_private', event.target.checked)}
                />
                Private repository
              </label>
            )}
            <label className="inline-flex items-center gap-2 font-medium text-slate-700">
              <input
                type="checkbox"
                checked={form.selected_only}
                onChange={(event) => updateForm('selected_only', event.target.checked)}
              />
              Selected scripts only
            </label>
            <label className="inline-flex items-center gap-2 font-medium text-slate-700">
              <input
                type="checkbox"
                checked={form.include_package_lock}
                onChange={(event) => updateForm('include_package_lock', event.target.checked)}
              />
              Include package-lock
            </label>
            <label className="inline-flex items-center gap-2 font-medium text-slate-700">
              <input
                type="checkbox"
                checked={form.include_run_results}
                onChange={(event) => updateForm('include_run_results', event.target.checked)}
              />
              Include run results
            </label>
          </div>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {error}
            </div>
          )}
          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              <span className="font-semibold">Pushed {result.files_exported} files.</span>
              {result.commit_url && (
                <a
                  href={result.commit_url}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 inline-flex items-center gap-1 font-semibold underline"
                >
                  View commit <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          )}
          {preview && (
            <div className="rounded-lg border border-slate-200 bg-white">
              <div className="border-b border-slate-200 px-3 py-2">
                <p className="text-sm font-semibold text-slate-900">
                  {preview.files_count || preview.exported_files?.length || 0} files prepared
                </p>
                {Array.isArray(preview.warnings) && preview.warnings.length > 0 && (
                  <p className="mt-1 text-xs text-amber-700">{preview.warnings.join(' ')}</p>
                )}
              </div>
              <div className="max-h-56 overflow-auto p-3">
                <ul className="space-y-1 text-xs font-mono text-slate-700">
                  {(preview.exported_files || []).map((filePath) => (
                    <li key={filePath} className="break-all">{filePath}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={previewExport}
            disabled={loading !== null}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
          >
            {loading === 'preview' ? <Loader className="h-4 w-4 animate-spin" /> : <FileCode className="h-4 w-4" />}
            Preview Files
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Close
            </button>
            <button
              type="button"
              onClick={pushExport}
              disabled={loading !== null}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {loading === 'export' ? <Loader className="h-4 w-4 animate-spin" /> : <Github className="h-4 w-4" />}
              Push
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
